"use client";

import { CustomSlider } from "@/components/ui/custom-slider";
import { useRef } from "react";

interface RentalValueSectionProps {
	value: { min: number; max: number };
	onChange: (value: { min: number; max: number }) => void;
	showTooltip?: boolean;
}

const rentalScale = [
	{ value: 20000, label: "20K" },
	{ value: 50000, label: "50K" },
	{ value: 100000, label: "1L" },
	{ value: 150000, label: "1.5L" },
	{ value: 200000, label: "2L+" },
];

const minRange = rentalScale[0].value;
const maxRange = rentalScale[rentalScale.length - 1].value;

// Move findClosestIndex outside component to make it completely stable
const findClosestIndex = (val: number) => {
	// Handle edge cases
	if (!val || val <= rentalScale[0].value) return 0;
	if (val >= rentalScale[rentalScale.length - 1].value) return rentalScale.length - 1;

	// Find exact match first
	const exactMatch = rentalScale.findIndex(item => item.value === val);
	if (exactMatch !== -1) return exactMatch;

	// Find closest value
	let closest = 0;
	let minDiff = Math.abs(rentalScale[0].value - val);

	for (let i = 1; i < rentalScale.length; i++) {
		const diff = Math.abs(rentalScale[i].value - val);
		if (diff < minDiff) {
			minDiff = diff;
			closest = i;
		}
	}

	return closest;
};

export function RentalValueSection({ value, onChange, showTooltip = true }: RentalValueSectionProps) {
	const currentMinValue = value?.min || minRange;
	const lastChangeRef = useRef<number | null>(null);

	// Use the stable function directly
	const currentSliderIndex = findClosestIndex(currentMinValue);

	// Add stability check - ensure index is always valid
	const safeSliderIndex = Math.max(0, Math.min(currentSliderIndex, rentalScale.length - 1));

	// Handle slider change with index-based logic
	const handleSliderChange = (newIndex: number) => {
		// Ensure newIndex is valid
		if (newIndex < 0 || newIndex >= rentalScale.length) {
			return;
		}

		const newMin = rentalScale[newIndex].value;

		// Prevent calling onChange if this is the same value we just set
		if (lastChangeRef.current === newMin) {
			return;
		}

		// Prevent calling onChange if the value hasn't actually changed from the prop
		if (newMin !== value?.min) {
			lastChangeRef.current = newMin;
			onChange({ min: newMin, max: maxRange });
		}
	};

	return (
		<div className="space-y-6">
			<div className="space-y-4">
				<div className="relative h-6 flex items-center px-3">
					<CustomSlider
						value={safeSliderIndex}
						onChange={newIndex => handleSliderChange(newIndex as number)}
						min={0}
						max={rentalScale.length - 1}
						step={1}
						ariaLabel="Minimum Rental Value"
					/>
				</div>
				{showTooltip && (
					<div className="flex justify-between text-xs text-gray-600 select-none pointer-events-none mb-2 px-3">
						{rentalScale.map((item, idx) => {
							const isSelected = idx === safeSliderIndex;
							return (
								<div
									key={idx}
									className="flex flex-col items-center gap-1"
									style={{
										minWidth: 36,
										paddingLeft: idx === 0 ? 0 : 0,
										paddingRight: idx === rentalScale.length - 1 ? 2 : 0,
									}}
								>
									<div className={`w-px h-2 ${isSelected ? "bg-primary-600" : "bg-gray-400"}`} />
									<span
										key={item.value}
										className={`text-xs font-semibold ${isSelected ? "text-primary-600" : "text-gray-600"}`}
										style={{ padding: "0 2px" }}
									>
										{item.label}
									</span>
								</div>
							);
						})}
					</div>
				)}

				{/* {description && (
						<Text slot="description" className="text-xs text-muted-foreground">
							{description}
						</Text>
					)} */}

				{/* {validationError && (
				<Text slot="errorMessage" className="text-sm font-medium text-destructive mt-2 block">
					{validationError}
				</Text>
			)} */}
			</div>
		</div>
	);
}

// export function RentalValueSection({ value, onChange }: RentalValueSectionProps) {
// 	const currentMinValue = value?.min || minRange;
// 	const [showTooltip, setShowTooltip] = useState(false);

// 	// Find the index of the scale item that is closest to (but not over) the current value
// 	const findClosestIndex = (val: number) => {
// 		return rentalScale.reduce((closestIndex, item, index) => {
// 			return item.value <= val ? index : closestIndex;
// 		}, 0);
// 	};
// 	const currentSliderIndex = findClosestIndex(currentMinValue);

// 	const handleSliderChange = (newIndices: [number]) => {
// 		const newIndex = newIndices[0];
// 		const newMin = rentalScale[newIndex].value;
// 		onChange({ min: newMin, max: maxRange });
// 	};

// 	// Format value for popover display
// 	const formatValueLabel = (index: number) => {
// 		const item = rentalScale[index];
// 		return item ? `> ${item.label}` : "";
// 	};

// 	const [validationError, setValidationError] = useState<string>("");
// 	const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
// 		const newMin = Number(e.target.value.replace(/[^0-9]/g, ""));
// 		if (newMin >= minRange && newMin <= maxRange) {
// 			setValidationError("");
// 			onChange({ min: newMin, max: maxRange });
// 		} else {
// 			setValidationError(`Value should be between ₹${formatCurrency(minRange)} and ₹${formatCurrency(maxRange)}`);
// 		}
// 	};

// 	const formatCurrency = (val: number) => {
// 		return new Intl.NumberFormat("en-IN").format(val);
// 	};

// 	return (
// 		<div className="space-y-6">
// 			{/* Radix Single Thumb Slider with Value Display */}
// 			<div className="relative pt-8">
// 				<div className="relative h-5">
// 					{/* Tooltip */}
// 					{showTooltip && (
// 						<div
// 							className="absolute -top-12 bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-10 pointer-events-none"
// 							style={{
// 								left: `${(currentSliderIndex / (rentalScale.length - 1)) * 100}%`,
// 								transform: "translateX(-50%)",
// 							}}
// 						>
// 							{formatValueLabel(currentSliderIndex)}
// 						</div>
// 					)}

// 					<Slider.Root
// 						className="relative flex items-center select-none touch-none w-full h-5"
// 						value={[currentSliderIndex]}
// 						onValueChange={handleSliderChange}
// 						onValueCommit={() => setShowTooltip(false)}
// 						min={0}
// 						max={rentalScale.length - 1}
// 						step={1}
// 					>
// 						<Slider.Track className="bg-gray-300 relative grow rounded-full h-1">
// 							<Slider.Range className="absolute bg-gray-500 rounded-full h-full" />
// 						</Slider.Track>
// 						<Slider.Thumb
// 							aria-label="Minimum Rental Value"
// 							className="block w-6 h-6 bg-white border-2 border-gray-500 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
// 							onMouseDown={() => setShowTooltip(true)}
// 							onTouchStart={() => setShowTooltip(true)}
// 						/>
// 					</Slider.Root>
// 				</div>
// 			</div>

// 			{/* Input Field */}
// 			<div className="grid grid-cols-1 gap-4 pt-2">
// 				<div>
// 					<label htmlFor="min-rental" className="block text-sm font-medium text-gray-700 mb-1">
// 						Min Value
// 					</label>
// 					<div className="relative">
// 						<span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">₹</span>
// 						<input
// 							id="min-rental"
// 							type="text"
// 							value={formatCurrency(currentMinValue)}
// 							onChange={handleMinInputChange}
// 							className={cn(
// 								"w-full pl-7 py-2 border border-gray-300 rounded-lg",
// 								"focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
// 								"disabled:bg-gray-50",
// 								"placeholder-gray-400 text-sm",
// 								"disabled:cursor-not-allowed disabled:opacity-50 md:text-sm [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
// 							)}
// 						/>
// 					</div>

// 					{/* {description && (
// 						<Text slot="description" className="text-xs text-muted-foreground">
// 							{description}
// 						</Text>
// 					)} */}

// 					{validationError && (
// 						<Text slot="errorMessage" className="text-sm font-medium text-destructive mt-2 block">
// 							{validationError}
// 						</Text>
// 					)}
// 				</div>
// 			</div>
// 		</div>
// 	);
// }
